import React from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { LocationProvider } from "./contexts/LocationContext";
import { IntegrationProvider } from "./contexts/IntegrationContext";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import RoleGuard from "components/RoleGuard";
import AppLayout from "./components/navigation/AppLayout";
import NotFound from "pages/NotFound";
import Login from "./pages/login";
import ForgotPassword from "./pages/forgot-password";
import ResetPassword from "./pages/reset-password";
import AcceptInvitation from "./pages/accept-invitation";
import DashboardOverview from "./pages/dashboard-overview";
import DailyTakingsAndTronc from "./pages/daily-takings-and-tronc";
import TroncDistribution from "./pages/tronc-distribution";
import DistributionRules from "./pages/rule-manager";
import DistributionLab from "./pages/distribution-lab";
import Employees from "./pages/employees";
import AuditTrail from "./pages/audit-trail";
import ProfileSettings from "./pages/settings/profile";
import CompanySettings from "./pages/settings/company";
import UserManagement from "./pages/user-management";
import TenantManagement from "./pages/settings/tenants";
import LocationManagement from "./pages/settings/locations";
import SyncLogsPage from "./pages/settings/sync-logs";
import MyTroncReport from "./pages/my-tronc";
import Transactions from "./pages/transactions";
import Reports from "./pages/reports";

// System Configuration Pages
import JobRolesPage from "./pages/system/job-roles";
import IntegrationsPage from "./pages/system/integrations";
import EligibilityCalculationSettings from "./pages/system/eligibility";
import ErrorLogsPage from "./pages/system/error-logs";
import PolicyGenerator from "./pages/policy-generator";

/**
 * Role constants for readability
 * RBAC Matrix:
 * - User:  My TRONC (coming soon), Policy (coming soon), Profile
 * - Manager:     Dashboard, Takings, Distribution, Employees, Policy (coming soon), Profile, Sync Logs
 * - TenantAdmin: Everything for their tenant
 * - GlobalAdmin: Everything except creating rules/distributions (backend enforced)
 */
const ALL_ROLES = ["User", "Manager", "TenantAdmin", "GlobalAdmin"];
const MANAGER_AND_ABOVE = ["Manager", "TenantAdmin", "GlobalAdmin"];
const ADMIN_AND_ABOVE = ["TenantAdmin", "GlobalAdmin"];
const GLOBAL_ADMIN_ONLY = ["GlobalAdmin"];

/**
 * Smart default redirect based on user role
 */
const DefaultRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "User") {
    return <Navigate to="/my-tronc" replace />;
  }
  return <Navigate to="/dashboard-overview" replace />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <LocationProvider>
            <IntegrationProvider>
              <ErrorBoundary>
                <ScrollToTop />
                <RouterRoutes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />

                  {/* Protected routes with AppLayout */}
                  <Route element={<AppLayout />}>
                    {/* Smart default redirect based on role */}
                    <Route
                      path="/"
                      element={<DefaultRedirect />}
                    />

                    {/* ========================================= */}
                    {/* MANAGER AND ABOVE: Core operational pages */}
                    {/* ========================================= */}
                    <Route
                      path="/dashboard-overview"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <DashboardOverview />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/daily-takings-and-tronc"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <DailyTakingsAndTronc />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/tronc-distribution"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <TroncDistribution />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/employees"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <Employees />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* ADMIN AND ABOVE: Admin operational pages */}
                    {/* ========================================= */}
                    <Route
                      path="/distribution-lab"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <DistributionLab />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/audit-trail"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <AuditTrail />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* COMING SOON pages (available to relevant roles) */}
                    {/* ========================================= */}
                    <Route
                      path="/policy-generator"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <PolicyGenerator />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/policy"
                      element={
                        <RoleGuard allowedRoles={ALL_ROLES} comingSoon>
                          <div />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <Reports />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/transactions"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <Transactions />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* ALL ROLES: Employee self-service */}
                    {/* ========================================= */}
                    <Route
                      path="/my-tronc"
                      element={
                        <RoleGuard allowedRoles={ALL_ROLES} comingSoon>
                          <MyTroncReport />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* ADMIN AND ABOVE: System Configuration */}
                    {/* ========================================= */}
                    <Route
                      path="/system/job-roles"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <JobRolesPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/eligibility"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <EligibilityCalculationSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/integrations"
                      element={
                        <RoleGuard allowedRoles={GLOBAL_ADMIN_ONLY}>
                          <IntegrationsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/system/error-logs"
                      element={
                        <RoleGuard allowedRoles={GLOBAL_ADMIN_ONLY}>
                          <ErrorLogsPage />
                        </RoleGuard>
                      }
                    />

                    {/* ========================================= */}
                    {/* Settings routes */}
                    {/* ========================================= */}
                    <Route
                      path="/settings/profile"
                      element={
                        <RoleGuard allowedRoles={ALL_ROLES}>
                          <ProfileSettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/company"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <CompanySettings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/locations"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <LocationManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/sync-logs"
                      element={
                        <RoleGuard allowedRoles={MANAGER_AND_ABOVE}>
                          <SyncLogsPage />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/users"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <UserManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/tenants"
                      element={
                        <RoleGuard allowedRoles={GLOBAL_ADMIN_ONLY}>
                          <TenantManagement />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/distribution-rules"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <DistributionRules />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/settings/job-roles"
                      element={
                        <RoleGuard allowedRoles={ADMIN_AND_ABOVE}>
                          <JobRolesPage />
                        </RoleGuard>
                      }
                    />

                    {/* Legacy redirects */}
                    <Route
                      path="/rule-manager"
                      element={
                        <Navigate to="/settings/distribution-rules" replace />
                      }
                    />
                    <Route
                      path="/settings/integrations"
                      element={<Navigate to="/system/integrations" replace />}
                    />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </RouterRoutes>
              </ErrorBoundary>
            </IntegrationProvider>
          </LocationProvider>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
